import { Box, CircularProgress, Typography } from "@mui/material";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function LoadingScreen({ label }) {
  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <Box textAlign="center">
        <CircularProgress size={34} />
        <Typography color="text.secondary" sx={{ mt: 1.5 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

const OFFLINE_READ_ROUTES = new Set(["/pos", "/products", "/inventory"]);

export default function ProtectedRoute({
  children,
  roles,
  platformRoles,
  requireTenantContext = true,
  requireLegalAcceptance = true,
}) {
  const {
    initializing,
    isAuthenticated,
    contextReady,
    workspaceOperational,
    role,
    platformRole,
    selectedWorkspace,
    legalStatusLoading,
    legalAcceptanceRequired,
    offlineAuthActive,
    connectivityState,
  } = useAuth();
  const location = useLocation();

  if (initializing) {
    return <LoadingScreen label="Restoring your secure session…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const offlineRouteAllowed = OFFLINE_READ_ROUTES.has(location.pathname);
  if (offlineAuthActive && !offlineRouteAllowed) {
    return <Navigate to="/pos" replace state={{ from: location, offlineLimited: true }} />;
  }

  const connectivityLimited = connectivityState === "offline"
    || connectivityState === "degraded";

  if (connectivityLimited && requireTenantContext && !offlineRouteAllowed) {
    return <Navigate to="/pos" replace state={{ from: location, offlineLimited: true }} />;
  }

  if (platformRoles && !platformRoles.includes(platformRole)) {
    return (
      <Navigate
        to={workspaceOperational ? "/dashboard" : "/onboarding"}
        replace
      />
    );
  }

  if (requireLegalAcceptance && selectedWorkspace && legalStatusLoading) {
    return <LoadingScreen label="Checking the current ABOS agreement…" />;
  }

  if (requireLegalAcceptance && selectedWorkspace && legalAcceptanceRequired) {
    return <Navigate to="/legal/accept" replace state={{ from: location }} />;
  }

  if (requireTenantContext && !contextReady) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3 }}>
        <Box textAlign="center" maxWidth={520}>
          <Typography variant="h5" fontWeight={900}>
            No store context is available
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Your account has no workspace and store configuration yet.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (requireTenantContext && !workspaceOperational) {
    return <Navigate to="/onboarding" replace />;
  }

  if (roles && !roles.includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
